import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { client, urlFor } from "../../../../services/client";

import { updateModuleProgressAction } from "../../../../actions/quizActions";
import InformationalStep from "./InformationalStep";
import InlineQuizStep from "./InlineQuizStep";
import ForestBackdrop from "../ForestBackdrop/ForestBackdrop";
import {
  deriveStepMeta,
  useArrowKeys,
  useLocalState,
  useScrollRestore,
} from "./playerUtils";
import styles from "./LearningPlayer.module.css";

import { LoadingTile } from "../../../../components/ui/Loading/LoadingTile";
import { useLoadingContent } from "../../../../hooks/useLoadingContent";
import { usePersistentLoading } from "../../../../hooks/usePersistentLoading";

export default function LearningPlayer() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loadingContent = useLoadingContent();

  const [moduleData, setModuleData] = useState(null);
  const hostRef = useRef(null);
  const readRef = useRef(null);

  useEffect(() => {
    if (!slug) return;
    const query = `*[_type == "module" && slug.current == $slug][0]{
      _id,
      title,
      "steps": steps[]->{ _id, stepType, body, internalTitle, quizOptions }
    }`;
    client
      .fetch(query, { slug })
      .then((data) => setModuleData(data))
      .catch(console.error);
  }, [slug]);

  // Per-module localStorage keys
  const sizeKey = `lp.${slug}.size`;
  const idxKey = `lp.${slug}.idx`;

  const [size, setSize] = useLocalState(sizeKey, 17);
  const [idx, setIdx] = useLocalState(idxKey, 0);

  const stepsLen = moduleData?.steps?.length || 0;

  // Clamp idx if module changes or progress was wiped
  useEffect(() => {
    if (stepsLen > 0 && idx >= stepsLen) setIdx(0);
  }, [stepsLen, idx, setIdx]);

  const derivedSteps = useMemo(() => {
    if (!moduleData?.steps) return [];
    return moduleData.steps.map((s, i) => deriveStepMeta(s, i));
  }, [moduleData]);

  const currentStep = moduleData?.steps?.[idx];
  const currentMeta = derivedSteps[idx];

  // Per-step scroll restoration for the reading column
  useScrollRestore(
    readRef,
    currentStep ? `lp.${slug}.scroll.${currentStep._id}` : null,
  );

  const goNext = () => {
    if (!moduleData?.steps) return;
    const isLast = idx >= moduleData.steps.length - 1;
    if (!isLast) {
      const newProgress = ((idx + 1) / moduleData.steps.length) * 100;
      dispatch(updateModuleProgressAction(moduleData._id, newProgress));
      setIdx(idx + 1);
    } else {
      dispatch(updateModuleProgressAction(moduleData._id, 100));
      navigate("/art-of-looking-menu");
    }
  };

  const goPrev = () => {
    if (idx > 0) setIdx(idx - 1);
    else navigate("/art-of-looking-menu");
  };

  useArrowKeys(hostRef, goNext, goPrev);

  const { isShowing, tileProps } = usePersistentLoading(
    !moduleData || !moduleData.steps || moduleData.steps.length === 0,
    loadingContent
  );

  if (isShowing) {
    return <LoadingTile content={loadingContent} {...tileProps} />;
  }

  const isLast = idx === moduleData.steps.length - 1;

  /* The roman-numeral eyebrow, its rule, the chapter tag and the "End of
     {chapter}" footer have all been removed. The heading carries the step on
     its own; the pearls below carry position. */
  const renderReadingColumn = () => {
    if (currentStep.stepType === "informational") {
      return (
        <>
          <h1 className={styles.heading}>{currentMeta.heading}</h1>
          <InformationalStep
            blocks={currentMeta.cleanedBody}
            fontSize={size}
          />
        </>
      );
    }

    if (currentStep.stepType === "multipleChoiceQuiz") {
      return (
        <InlineQuizStep
          step={currentStep}
          heading={currentMeta.heading}
          fontSize={size}
        />
      );
    }

    return (
      <>
        <h1 className={styles.heading}>{currentMeta.heading}</h1>
        <div className={styles.fallbackCard}>
          This step type isn't supported in the player yet. Continue when ready.
        </div>
      </>
    );
  };

  const focalImage = currentMeta.focalImage;

  return (
    <ForestBackdrop>
      <div
        ref={hostRef}
        className={styles.pageWrapper}
        tabIndex={-1}
        aria-label={`${moduleData.title} — ${currentMeta.chapter}`}
      >
        <div>
          {/* Toolbar (Aa size) */}
          <div className={styles.toolbar}>
            <div className={styles.sizeGroup}>
              <button
                type="button"
                className={styles.sizeBtn}
                onClick={() => setSize((s) => Math.max(15, s - 1))}
                aria-label="Decrease text size"
              >
                A
              </button>
              <button
                type="button"
                className={`${styles.sizeBtn} ${styles.sizeBtnBig}`}
                onClick={() => setSize((s) => Math.min(22, s + 1))}
                aria-label="Increase text size"
              >
                A
              </button>
            </div>
          </div>

          {/* Three-column stage */}
          <div key={currentStep._id} className={styles.stage}>
            {/* Chapter rail */}
            <nav className={styles.rail} aria-label="Chapter rail">
              {derivedSteps.map((m, i) => {
                const isCur = i === idx;
                const isDone = i < idx;
                return (
                  <button
                    type="button"
                    key={moduleData.steps[i]._id}
                    onClick={() => setIdx(i)}
                    className={`${styles.railItem} ${
                      isCur
                        ? styles.railItemCurrent
                        : isDone
                          ? styles.railItemDone
                          : ""
                    }`}
                    title={m.chapter}
                  >
                    <span
                      className={`${styles.railTick} ${
                        isDone || isCur ? styles.railTickActive : ""
                      }`}
                    />
                    <span className={styles.railLabel}>{m.chapter}</span>
                  </button>
                );
              })}
            </nav>

            {/* Focal image plate */}
            <figure className={styles.figure}>
              <div className={styles.plate}>
                {focalImage ? (
                  <img
                    src={urlFor(focalImage.asset).width(900).url()}
                    alt={focalImage.alt || currentMeta.chapter}
                    className={styles.plateImg}
                  />
                ) : (
                  <div className={styles.placeholderPlate} aria-hidden="true" />
                )}
              </div>
              {/* One caption, not three. The "Fig. 01" stamp over the image and
                  the "Plate 01" prefix here were the same zero-padded numbering
                  §14a names, restating a position the rail already shows. */}
              <figcaption className={styles.caption}>
                <span>{currentMeta.chapter}</span>
                {focalImage?.alt && (
                  <span className={styles.captionHint}>{focalImage.alt}</span>
                )}
              </figcaption>
            </figure>

            {/* Reading column */}
            <div ref={readRef} className={styles.read}>
              {renderReadingColumn()}
            </div>
          </div>

          {/* Bottom bar */}
          <div className={styles.bottomBar}>
            <button
              type="button"
              onClick={goPrev}
              disabled={idx === 0}
              className={`${styles.backBtn} ${
                idx === 0 ? styles.backBtnDisabled : ""
              }`}
            >
              ← Back
            </button>
            <div className={styles.pearls}>
              {moduleData.steps.map((s, i) => (
                <button
                  type="button"
                  key={s._id}
                  onClick={() => setIdx(i)}
                  className={`${styles.pearl} ${
                    i === idx
                      ? styles.pearlCurrent
                      : i < idx
                        ? styles.pearlDone
                        : ""
                  }`}
                  aria-label={`Go to step ${i + 1}`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={goNext}
              className={styles.continueBtn}
            >
              {isLast ? "Mark complete" : "Continue"}
            </button>
          </div>
        </div>
      </div>
    </ForestBackdrop>
  );
}
